import { zodResolver } from '@hookform/resolvers/zod';
import {
  IInvoiceTermsSettingsSchema,
  InvoiceTermsSettingsSchema,
} from '@monetize/types/app';
import { useEffect, useState } from 'react';
import {
  Control,
  Controller,
  FieldErrors,
  UseFormHandleSubmit,
  useForm,
} from 'react-hook-form';
import { Link as ReactDomLink } from 'react-router-dom';
import { handleApiErrorToast } from '../../../../api/axios';
import {
  AsyncStatusIndicator,
  MAccordion,
  MAccordionCustomButton,
  MAccordionCustomButtonItem,
  MBox,
  MButton,
  MFlex,
  MFormField,
  MGrid,
  MGridItem,
  MLink,
  MStack,
  MSwitch,
  MText,
  TemplateExpressionHelp,
} from '../../../../components/Monetize';
import MEditor from '../../../../components/Monetize/MEditor';
import { MSettingAccordionItem } from '../../../../components/Monetize/MSettingAccordionItem';
import { TemplateExpressionWarning } from '../../../../components/Monetize/TemplateExpressionWarning';
import { ROUTES } from '../../../../constants';
import { useAccordionControl } from '../../../../hooks';
import { useAsyncStatus } from '../../../../hooks/useAsyncStatus';
import {
  getHighlightStyles,
  useSettingsSectionHighlight,
} from '../../../../hooks/useSettingsSectionHighlight';
import { useFlags } from '../../../../services/launchDarkly';

export type InvoiceTermsProps = {
  invoiceTerms: IInvoiceTermsSettingsSchema;
  onSubmit: (data: IInvoiceTermsSettingsSchema) => Promise<void>;
};

const CollapsedStatus = ({
  isInvoiceTermsActive,
}: {
  isInvoiceTermsActive: boolean;
}) => {
  return (
    <MAccordionCustomButtonItem
      label="Status:"
      value={isInvoiceTermsActive ? 'Active' : 'Inactive'}
      w="70px"
    />
  );
};

const ToggleSwitch = ({
  errors,
  control,
  handleSubmitTerms,
  handleSubmit,
}: {
  errors: FieldErrors<IInvoiceTermsSettingsSchema>;
  control: Control<IInvoiceTermsSettingsSchema>;
  handleSubmitTerms: (data: IInvoiceTermsSettingsSchema) => Promise<void>;
  handleSubmit: UseFormHandleSubmit<IInvoiceTermsSettingsSchema>;
}) => {
  const { status, track } = useAsyncStatus();
  return (
    <MFormField
      error={errors?.invoiceTermsActive}
      onClick={(ev) => ev.stopPropagation()}
    >
      <Controller
        name="invoiceTermsActive"
        control={control}
        render={({ field: { value, onChange, ...rest } }) => (
          <MBox position="relative">
            <MSwitch
              isChecked={value}
              isDisabled={status === 'loading'}
              onChange={(ev: React.ChangeEvent<HTMLInputElement>) => {
                onChange(ev.target.checked);
                track(() => handleSubmit(handleSubmitTerms)());
              }}
              {...rest}
            />
            <AsyncStatusIndicator status={status} />
          </MBox>
        )}
      />
    </MFormField>
  );
};

const InvoiceTermsAccordionButton = ({
  isExpanded,
  isInvoiceTermsActive,
  enableLegalEntityInvoiceTerms,
  errors,
  control,
  handleSubmitTerms,
  handleSubmit,
}: {
  isExpanded: boolean;
  isInvoiceTermsActive: boolean;
  enableLegalEntityInvoiceTerms: boolean;
  errors: FieldErrors<IInvoiceTermsSettingsSchema>;
  control: Control<IInvoiceTermsSettingsSchema>;
  handleSubmitTerms: (data: IInvoiceTermsSettingsSchema) => Promise<void>;
  handleSubmit: UseFormHandleSubmit<IInvoiceTermsSettingsSchema>;
}) => {
  return (
    <MAccordionCustomButton
      isExpanded={isExpanded}
      label={
        enableLegalEntityInvoiceTerms
          ? 'Default Invoice Terms'
          : 'Invoice Terms'
      }
    >
      {!isExpanded ? (
        <CollapsedStatus isInvoiceTermsActive={isInvoiceTermsActive} />
      ) : (
        <ToggleSwitch
          errors={errors}
          control={control}
          handleSubmitTerms={handleSubmitTerms}
          handleSubmit={handleSubmit}
        />
      )}
    </MAccordionCustomButton>
  );
};

const AccordionContent = ({
  errors,
  control,
  enableLegalEntityInvoiceTerms,
  isInvoiceTermsActive,
  isSaveBtnDisabled,
  isLoading,
  handleSubmitTerms,
  handleSubmit,
}: {
  errors: FieldErrors<IInvoiceTermsSettingsSchema>;
  control: Control<IInvoiceTermsSettingsSchema>;
  enableLegalEntityInvoiceTerms: boolean;
  isInvoiceTermsActive: boolean;
  isSaveBtnDisabled: boolean;
  isLoading: boolean;
  handleSubmitTerms: (data: IInvoiceTermsSettingsSchema) => Promise<void>;
  handleSubmit: UseFormHandleSubmit<IInvoiceTermsSettingsSchema>;
}) => {
  return (
    <MBox mt={2}>
      <MGrid templateColumns="repeat(12, 1fr)" gap={2}>
        <MGridItem colSpan={12}>
          <MFormField error={errors.invoiceTerms}>
            <Controller
              name="invoiceTerms"
              control={control}
              render={({ field: { onChange, value, ...rest } }) => (
                <>
                  <MEditor
                    autoScroll
                    handleEditorChange={onChange}
                    boxProps={{ pb: 0, minHeight: 125 }}
                    init={{ height: 125 }}
                    disabled={!isInvoiceTermsActive}
                    value={value ?? ''}
                    {...rest}
                  />
                  <TemplateExpressionHelp
                    documentType="invoice"
                    testId="invoice-terms-settings-help-btn"
                  />
                  <TemplateExpressionWarning value={value} />
                </>
              )}
            />
          </MFormField>
        </MGridItem>
      </MGrid>
      <MGridItem colSpan={12} mt={8}>
        <MFlex justify="end">
          <MButton
            variant="primary"
            onClick={handleSubmit(handleSubmitTerms)}
            isDisabled={isSaveBtnDisabled}
            isLoading={isLoading}
          >
            Save
          </MButton>
        </MFlex>
      </MGridItem>
    </MBox>
  );
};

export const InvoiceTerms = ({ invoiceTerms, onSubmit }: InvoiceTermsProps) => {
  const { enableLegalEntityInvoiceTerms } = useFlags();
  const sectionKey = enableLegalEntityInvoiceTerms
    ? 'defaultInvoiceTerms'
    : 'invoiceTerms';
  const isHighlighted = useSettingsSectionHighlight(sectionKey);
  const [isLoading, setIsLoading] = useState(false);
  const { accordionValue, handleAccordionChange, setAccordionValue } =
    useAccordionControl();

  useEffect(() => {
    if (isHighlighted) {
      setAccordionValue(['0']);
    }
  }, [isHighlighted, setAccordionValue]);

  const {
    handleSubmit,
    control,
    formState: { errors, isDirty, isValid },
    reset,
    watch,
  } = useForm<IInvoiceTermsSettingsSchema>({
    resolver: zodResolver(InvoiceTermsSettingsSchema),
    mode: 'onChange',
    defaultValues: {
      invoiceTermsActive: false,
      invoiceTerms: '',
    },
  });

  useEffect(() => {
    if (invoiceTerms) {
      reset({
        ...invoiceTerms,
        invoiceTerms: invoiceTerms.invoiceTerms ?? '',
        invoiceTermsActive: invoiceTerms.invoiceTermsActive,
      });
    } else {
      reset({
        invoiceTermsActive: false,
        invoiceTerms: '',
      });
    }
  }, [invoiceTerms, reset]);

  const handleSubmitTerms = async (data: IInvoiceTermsSettingsSchema) => {
    try {
      setIsLoading(true);
      await onSubmit(data);
    } catch (err: any) {
      handleApiErrorToast(err);
    } finally {
      setIsLoading(false);
    }
  };

  const isInvoiceTermsActive = watch('invoiceTermsActive');
  const isSaveBtnDisabled =
    !isDirty || !isValid || !isInvoiceTermsActive || isLoading;

  return (
    <MStack w="100%" data-testid="approval-notes-config">
      <form
        onSubmit={handleSubmit(handleSubmitTerms)}
        data-testid="quote-setting-form"
      >
        <MAccordion
          multiple
          value={accordionValue}
          onValueChange={({ value }) => handleAccordionChange(value)}
        >
          <MSettingAccordionItem
            value="0"
            id={sectionKey}
            description="These terms will be added to a section at the bottom of invoices."
            {...getHighlightStyles(isHighlighted)}
            borderWidth="1px"
            renderAccordionButton={({ isExpanded }) => (
              <InvoiceTermsAccordionButton
                isExpanded={isExpanded}
                isInvoiceTermsActive={isInvoiceTermsActive}
                enableLegalEntityInvoiceTerms={enableLegalEntityInvoiceTerms}
                errors={errors}
                control={control}
                handleSubmitTerms={handleSubmitTerms}
                handleSubmit={handleSubmit}
              />
            )}
          >
            <MText mb={4}>
              To override these terms for a specific legal entity or currency,
              go to the{' '}
              <MLink fontSize="sm" asChild>
                <ReactDomLink to={ROUTES.COMPANY_SETTINGS}>
                  Legal Entities
                </ReactDomLink>
              </MLink>{' '}
              page
            </MText>
            <AccordionContent
              errors={errors}
              control={control}
              enableLegalEntityInvoiceTerms={enableLegalEntityInvoiceTerms}
              isInvoiceTermsActive={isInvoiceTermsActive}
              isSaveBtnDisabled={isSaveBtnDisabled}
              isLoading={isLoading}
              handleSubmitTerms={handleSubmitTerms}
              handleSubmit={handleSubmit}
            />
          </MSettingAccordionItem>
        </MAccordion>
      </form>
    </MStack>
  );
};
